/**  @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Boundary } from '@floating-ui/react-dom';

import Forward10Svg from 'bundles/video-player/assets/svg/Forward10Svg';
import Replay10Svg from 'bundles/video-player/assets/svg/Replay10Svg';
import ModernTooltipWrapper from 'bundles/video-player/components/ModernTooltipWrapper';
import type { VideoPlayerEventingV3UIFunction } from 'bundles/video-player/types/VideoPlayer';
import { useVideoTrimmingContext } from 'bundles/video-trim';

import _t from 'i18n!nls/video-player';

const styles = {
  seekButton: () => css`
    margin-left: var(--cds-spacing-200);
  `,
  right: css`
    margin-right: auto;
  `,
};

type Props = {
  player: any;
  videoBoundaryElement?: Boundary;
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
};

type SeekControlProps = {
  direction: number;
  seekTime: number;
  icon: React.ReactElement;
  label: string;
  tooltipMessage: string;
  keyboardShortcut: string;
} & Props;

export const SeekButton = ({
  direction,
  seekTime,
  player,
  icon,
  label,
  videoBoundaryElement,
  tooltipMessage,
  keyboardShortcut,
  trackVideoPlayerClickEvent,
}: SeekControlProps) => {
  const { trimmedStartTimeSeconds, trimmedEndTimeSeconds } = useVideoTrimmingContext();

  const handleSeekVideoClick = () => {
    const previousVideoPosition = player.currentTime();
    // Calculate the new time based on the current time and direction
    let newTime = previousVideoPosition + direction * seekTime;
    if (newTime < trimmedStartTimeSeconds) {
      newTime = trimmedStartTimeSeconds;
    }
    if (trimmedEndTimeSeconds && newTime > trimmedEndTimeSeconds) {
      newTime = trimmedEndTimeSeconds;
    }

    if (previousVideoPosition) {
      trackVideoPlayerClickEvent(direction > 0 ? 'video_seek_forward' : 'video_seek_backward', {
        videoPosition: newTime,
        previousVideoPosition,
      });
    }

    // Set the player's time to the new time
    player.currentTime(newTime);
  };

  return (
    <ModernTooltipWrapper
      placement="top"
      message={tooltipMessage}
      videoBoundaryElement={videoBoundaryElement}
      keyboardShortcut={keyboardShortcut}
    >
      <button aria-label={label} type="button" onClick={handleSeekVideoClick} css={styles.seekButton}>
        {icon}
      </button>
    </ModernTooltipWrapper>
  );
};

export const SeekForwardControl = ({ player, videoBoundaryElement, trackVideoPlayerClickEvent }: Props) => (
  <SeekButton
    label={_t('Seek Video Forward 10 seconds')}
    player={player}
    seekTime={10}
    direction={1}
    icon={<Forward10Svg width={28} height={28} />}
    videoBoundaryElement={videoBoundaryElement}
    tooltipMessage={_t('Forward 10s')}
    keyboardShortcut="right"
    trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
  />
);

export const SeekBackwardControl = ({ player, videoBoundaryElement, trackVideoPlayerClickEvent }: Props) => (
  <SeekButton
    label={_t('Seek Video Backward 10 seconds')}
    player={player}
    seekTime={10}
    direction={-1}
    icon={<Replay10Svg width={28} height={28} />}
    videoBoundaryElement={videoBoundaryElement}
    tooltipMessage={_t('Backward 10s')}
    keyboardShortcut="left"
    trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
  />
);
